import type { BusinessHours } from './formDefinitions'

/** Days that share one opening time and one closing time. */
export interface HoursGroup {
  days: string[]
  opens: string
  closes: string
}

/**
 * Stored hours stay one row per day; the editor shows them as groups. Rows
 * sharing a time range become one group, in week order, and closed days
 * belong to none. A day with no times belongs to none on a fresh form, but
 * once any day is closed or timed the rows came from the editor, so a day
 * still waiting for times is a group whose times are blank.
 */
export function groupsFromRows(rows: BusinessHours[]): HoursGroup[] {
  const edited = rows.some(
    (row) => row.closed === true || Boolean(row.opens) || Boolean(row.closes),
  )
  const groups: HoursGroup[] = []
  for (const row of rows) {
    const opens = row.opens ?? ''
    const closes = row.closes ?? ''
    if (row.closed === true || (!edited && opens === '' && closes === '')) continue
    const match = groups.find((group) => group.opens === opens && group.closes === closes)
    if (match) match.days.push(row.day)
    else groups.push({ days: [row.day], opens, closes })
  }
  return groups
}

/** Every day in a group opens for that group's times; every other day is closed. */
export function rowsFromGroups(days: string[], groups: HoursGroup[]): BusinessHours[] {
  return days.map((day) => {
    const group = groups.find((candidate) => candidate.days.includes(day))
    return group
      ? { day, opens: group.opens, closes: group.closes, closed: false }
      : { day, opens: '', closes: '', closed: true }
  })
}
